import {yupResolver} from '@hookform/resolvers/yup'
import {Stack} from '@mui/material'
import React, {useImperativeHandle, useEffect} from 'react'
import {
  useForm,
  FormProvider,
  type FieldValues,
  type SubmitHandler,
  DefaultValues
} from 'react-hook-form'
import {object as yupObject, type AnyObjectSchema} from 'yup'

import {useSteppedFormContext} from '../hooks'

export type StepFormRef = {
  submit: () => void
  getValues: () => FieldValues
}

type StepComponentProps<TStepFieldValues extends FieldValues> = {
  validationSchema?: AnyObjectSchema
  defaultValues?: DefaultValues<TStepFieldValues>
  values?: TStepFieldValues
  readOnly?: boolean
  children: React.ReactNode
}

const StepFormInner = <TStepFieldValues extends FieldValues>(
  {
    validationSchema = yupObject(),
    defaultValues,
    values,
    readOnly,
    children
  }: StepComponentProps<TStepFieldValues>,
  ref: React.Ref<StepFormRef>
) => {
  const {
    updateCurrentStepValues,
    stepActions,
    updateCurrentStepValuesAndSubmit,
    setCurrentStepDirty
  } = useSteppedFormContext()
  const methods = useForm<TStepFieldValues>({
    resolver: readOnly ? undefined : yupResolver(validationSchema),
    values,
    ...(!readOnly && {defaultValues})
  })

  const {
    formState: {isDirty}
  } = methods

  useEffect(() => {
    setCurrentStepDirty(isDirty)
  }, [isDirty, setCurrentStepDirty])

  const handleSubmissionAfterAllSteps: SubmitHandler<TStepFieldValues> = (data) => {
    if (stepActions.canGoToNextStep) {
      updateCurrentStepValues(data)
      stepActions.goToNextStep()
    } else {
      updateCurrentStepValuesAndSubmit(data)
    }
  }

  const handleSubmissionAtAnyStep: SubmitHandler<TStepFieldValues> = (data) => {
    updateCurrentStepValuesAndSubmit(data)
  }

  useImperativeHandle(ref, () => ({
    submit: () => {
      void methods.handleSubmit((data) => {
        handleSubmissionAtAnyStep(data)
      })()
    },
    getValues: () => methods.getValues()
  }))

  return (
    <FormProvider {...methods}>
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form onSubmit={methods.handleSubmit(handleSubmissionAfterAllSteps)} style={{width: '100%'}}>
        <Stack spacing={2} alignItems="center" useFlexGap>
          {children}
        </Stack>
      </form>
    </FormProvider>
  )
}

export const StepForm = React.forwardRef(StepFormInner) as <T extends FieldValues>(
  props: StepComponentProps<T> & {ref?: React.Ref<StepFormRef>}
) => React.ReactElement
